import { lineHeightCrop } from '@monobits/core';
import { defaultScales } from '@monobits/preset-base';
import { pick, merge } from 'lodash';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}
/**
 * Set fonts from an object along with its props
 *
 * @param {Object} fontlist Object with fonts and their values (family, fallbacks, crop)
 * @param {any} props Additionnal props are simply returned
 * @returns {Object} Props plus font properties
 */


var makeFonts = function makeFonts(fontlist, props) {
  return {
    fonts: _extends({}, Object.entries(fontlist).reduce(function (acc, _ref) {
      var _extends2;

      var key = _ref[0],
          val = _ref[1];
      return _extends({}, acc, (_extends2 = {}, _extends2[key] = [val.family, val.fallbacks].join(', '), _extends2));
    }, {}), props)
  };
};

var _excluded$1 = ["crops", "lineHeight"],
    _excluded2$1 = ["lineHeight", "crops"];
/**
 * Set font style with crop values
 *
 * @param {string} name Name/key of the object
 * @param {Object} cssValues
 * @param {Array.<number>} cssValues.crops Crops values
 * @param {string|number} cssValues.lineHeight CSS line-height value
 * @param {...any} cssValues.props Rest of the props
 * @returns {object} Returns an object with crop values
 */

var withCrop = function withCrop(name, _ref) {
  var _ref3;

  var crops = _ref.crops,
      line = _ref.lineHeight,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$1);

  var print = function print(prop) {
    if (Array.isArray(line)) return line.map(function (value) {
      return lineHeightCrop(crops, value)[prop];
    });
    return lineHeightCrop(crops, line)[prop];
  };

  if (crops != null) {
    var _ref2;

    return _ref2 = {}, _ref2[name] = _extends({}, props, {
      lineHeight: print('lineHeight'),
      '::before,::after': {
        content: '""',
        display: 'block'
      },
      '::after': {
        mt: print('top')
      },
      '::before': {
        mb: print('bot')
      }
    }), _ref2;
  }

  return _ref3 = {}, _ref3[name] = _extends({}, props), _ref3;
};
/**
 * Accepts multiple objects and sets crop to each of them
 * @see withCrop
 *
 * @param  {object} scales Object with theme-ui font scales
 * @param  {object} fontlist=[] Object with fonts and their values (family, fallbacks, crop)
 * @param  {object} {...object} Main object
 * @returns {object} Returns an object (with nesting) with crop values
 */


var makeCrops = function makeCrops(_temp, _ref4) {
  var _ref5 = _temp === void 0 ? [] : _temp,
      scales = _ref5[0],
      fontlist = _ref5[1];

  var object = _extends({}, _ref4);

  var _value = function _value(ini, obj) {
    return ini in obj ? obj[ini] : ini;
  };

  var _line = scales.lineHeights;

  var format = function format(key) {
    var _object$key = object[key],
        lineHeight = _object$key.lineHeight,
        crops = _object$key.crops,
        props = _objectWithoutPropertiesLoose(_object$key, _excluded2$1);

    return withCrop(key, _extends({}, props, {
      lineHeight: _value(lineHeight, _line),
      crops: _value(crops, fontlist).crop
    }));
  };

  return Object.keys(object).reduce(function (acc, key) {
    if (typeof acc === 'string') {
      return _extends({}, format(acc), format(key));
    }

    return _extends({}, acc, format(key));
  });
};

var _excluded = ["fonts", "variants"],
    _excluded2 = ["__cropped", "key"];
/**
 * Builds font theme
 *
 * @param {Array.<Object>} arrayProperties
 * @param {Object} arrayProperties.fontlist Object with fonts and their values (family, fallbacks, crop)
 * @param {Object} arrayProperties.scales Object with theme-ui font scales
 * @param {Object} propsValues
 * @param {Object} propsValues.fonts Theme UI `fonts` object
 * @param {Object} propsValues.variants Object containing all the variants objects
 * @param {...any} propsValues.props Rest of the props
 * @return {Object} A theme-ui font object
 */

var buildFonts = function buildFonts(_ref, _ref2) {
  var _extends2;

  var fontlist = _ref[0],
      _ref$ = _ref[1],
      scales = _ref$ === void 0 ? defaultScales : _ref$;

  var fonts = _ref2.fonts,
      variants = _ref2.variants,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded);

  var _variants$__cropped = variants.__cropped,
      __cropped = _variants$__cropped === void 0 ? null : _variants$__cropped,
      _variants$key = variants.key,
      key = _variants$key === void 0 ? 'text' : _variants$key,
      presets = _objectWithoutPropertiesLoose(variants, _excluded2);

  return _extends({}, props, scales, makeFonts(fontlist, fonts), (_extends2 = {}, _extends2[key] = _extends({}, presets, __cropped && makeCrops([scales, fontlist], __cropped)), _extends2));
};
/**
 * List of fonts wit crop values
 * @type {object}
 */


var __consts = {
  helvetica: {
    family: 'helvetica',
    fallbacks: 'arial, sans-serif',
    crop: [9, 9]
  },
  now: {
    family: 'Helvetica Now Display',
    fallbacks: 'helvetica, arial, sans-serif',
    crop: [9, 8]
  },
  suisse: {
    family: 'Suisse BP',
    fallbacks: 'helvetica, arial, sans-serif',
    crop: [3, 11]
  }
};
/**
 * Reduces a list of fonts from an array of keys
 *
 * @param  {array<srting>} selected=[] An array of selected object keys
 * @param  {object} list=[] A custom fonts object that will be merged with the default one
 * @returns {object} A curated font list object
 */

var fontlist = function fontlist(selected, list) {
  if (selected === void 0) {
    selected = [];
  }

  if (list === void 0) {
    list = {};
  }

  return pick(merge(__consts, list), selected);
};

export { buildFonts, fontlist, makeCrops, makeFonts };